/* eslint-disable @typescript-eslint/no-unused-expressions */
/* eslint-disable no-lone-blocks */
import {
  forwardRef,
  Ref,
  ChangeEvent,
  useEffect,
  useReducer,
  useState,
  useImperativeHandle,
  FormEvent,
  useCallback,
} from 'react';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import { validate } from '../../../utils/validators';
import * as Styled from './styles';
import { validatorType } from '../../../utils/validators';
import { phoneMask, registeredNumberMask, zipCodeMask } from './InputMasksFunctions';

export type InputForwardType = {
  touchHandler: () => void;
  changeHandler: (value: string) => void;
};

type InputProps = {
  id: string;
  type: string;
  value?: string;
  maxLength?: number;
  label?: string;
  placeholder?: string;
  errorText?: string;
  errorText2?: string;
  error2?: boolean;
  errorText3?: string;
  error3?: boolean;
  validators?: validatorType[];
  initialValue?: string;
  initialValid?: boolean;
  isLogin?: boolean;
  onInput?: (id: string, value: string, isValid: boolean) => void;
  onBlur?: (e: any) => void;
  onFocus?: (e: any) => void;
  disabled?: boolean;
  maskType?: 'ZIP_CODE' | 'REGISTERED_NUMBER' | 'PHONE_NUMBER';
  onChange?: (value: string) => string;
  onChangeInputSave?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
};

type StateType = {
  value: string;
  isValid: boolean;
  isTouched: boolean;
};

type ChangeActionType = {
  type: 'CHANGE';
  payload: {
    value: string;
    validators: validatorType[] | undefined;
  };
};

type TouchActionType = {
  type: 'TOUCH';
};
const inputReducer = (
  state: StateType,
  action: ChangeActionType | TouchActionType
) => {
  switch (action.type) {
    case 'CHANGE':
      return {
        ...state,
        value: action.payload.value,
        isValid: action.payload.validators
          ? validate(action.payload.value, action.payload.validators)
          : true,
      };
    case 'TOUCH':
      return {
        ...state,
        isTouched: true,
      };

    default:
      return state;
  }
};

const InputForwardRef = forwardRef<InputForwardType, InputProps>(
  (props: InputProps, ref: Ref<InputForwardType>) => {
    const [state, dispatch] = useReducer(inputReducer, {
      value: props.initialValue || '',
      isValid: props.initialValid || false,
      isTouched: false,
    });
    const [showPassword, setShowPassword] = useState(false);

    const { id, onInput } = props;
    const { value, isValid } = state;

    useEffect(() => {
      changeHandler(state.value);
    }, [props.validators]);

    useEffect(() => {
      {
        onInput ? onInput(id, value, isValid) : '';
      }
    }, [id, value, isValid, onInput]);

    const changeHandler = (value: string) => {
      dispatch({
        type: 'CHANGE',
        payload: {
          value: props.onChange ? props.onChange(value) : value,
          validators: props.validators,
        },
      });
    };

    const touchHandler = () => {
      dispatch({ type: 'TOUCH' });
    };

    const keyUpHandler = useCallback((event: FormEvent<HTMLInputElement>) => {
      if (!props.maskType) return;
      if (props.maskType === 'ZIP_CODE') zipCodeMask(event);
      if (props.maskType === 'REGISTERED_NUMBER') registeredNumberMask(event);
      if (props.maskType === 'PHONE_NUMBER') phoneMask(event);
    }, [props.maskType]);

    useImperativeHandle(ref, () => ({ touchHandler, changeHandler }));

    const InputPassword = (
      <Styled.InputPassword className={props.isLogin ? 'white-theme' : ''}>
        <input
          type={showPassword ? 'text' : 'password'}
          id={props.id}
          placeholder={props.placeholder}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            changeHandler(event.target.value);
          }}
          onBlur={(e) => {
            touchHandler();
            if (props.onBlur !== undefined) {
              props.onBlur(e);
            }
          }}
          value={state.value}
          autoComplete='off'
        />
        {!showPassword && (
          <VisibilityOff
            cursor='pointer'
            color='disabled'
            onClick={() => setShowPassword(true)}
          />
        )}
        {showPassword && (
          <Visibility
            cursor='pointer'
            color='disabled'
            onClick={() => setShowPassword(false)}
          />
        )}
      </Styled.InputPassword>
    );

    const Input = (
      <Styled.Input
        className={`${(!state.isValid && state.isTouched) || props.error2 || props.error3 ? 'invalid' : ''
          } ${props.isLogin ? 'white-theme' : ''}`}
        id={props.id}
        type={props.type}
        placeholder={props.placeholder}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          changeHandler(event.target.value);
          props.onChangeInputSave && props.onChangeInputSave(event);
        }}
        onBlur={(e) => {
          touchHandler();
          if (props.onBlur !== undefined) {
            props.onBlur(e);
          }
        }}
        onFocus={(e) => {
          if (props.onFocus !== undefined) {
            props.onFocus(e);
          }
        }}
        onKeyDown={props.onKeyDown}
        value={props.value ? props.value : state.value}
        autoComplete='off'
        disabled={props.disabled}
        onKeyUp={keyUpHandler}
        maxLength={props.maxLength}
      />
    );

    const InputArea = (
      <Styled.InputArea
        className={`${(!state.isValid && state.isTouched) || props.error2 || props.error3 ? 'invalid' : ''
          } ${props.isLogin ? 'white-theme' : ''}`}
        id={props.id}
        placeholder={props.placeholder}
        value={props.value ? props.value : state.value}
        onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
          changeHandler(event.target.value);
        }}
        onBlur={(e) => {
          touchHandler();
          if (props.onBlur !== undefined) {
            props.onBlur(e);
          }
        }}
      />
    );

    return (
      <Styled.Container>
        {props.label ? (
          <Styled.Label
            htmlFor={props.id}
            className={props.isLogin ? 'white-theme' : ''}
          >
            {props.label}
          </Styled.Label>
        ) : (
          ''
        )}
        <Styled.Content>
          {props.type === 'password' && InputPassword}
          {props.type === 'textArea' && InputArea}
          {props.type !== 'password' && props.type !== 'textArea' && Input}

          {!state.isValid && state.isTouched && (
            <Styled.Small className={props.isLogin ? 'white-theme' : ''}>
              {props.errorText}
            </Styled.Small>
          )}
          {props.error2 && !state.isTouched && (
            <Styled.Small className={props.isLogin ? 'white-theme' : ''}>
              {props.errorText2}
            </Styled.Small>
          )}
          {props.error3 && (
            <Styled.Small className={props.isLogin ? 'white-theme' : ''}>
              {props.errorText3}
            </Styled.Small>
          )}
        </Styled.Content>
      </Styled.Container>
    );
  }
);
export default InputForwardRef;
